/**
 * Feature E02-F01-T06 — "New Project" button (+ create flow via createManualProject).
 *
 * 1:1 port of `../EER app/public/assets/js/dashboard.js → createManualProject`:
 * the button POSTs `create_manual` (an empty stage-1 project, role/client edited
 * later in the card), and on a `{ success, project_id }` reply it toasts
 * "New project created", lets the list invalidate (the mutation does it), and
 * OPENS the new project (`onCreated(project_id)` → router navigate /project/:id).
 *
 * While the request is in flight the button is disabled and shows a spinner with
 * "Creating…"; it restores in the mutation's settle (finally parity). On a failed
 * reply (`success !== true` or no id, OR a transport error) it toasts
 * `data.error || 'Error creating project'`.
 *
 * E12-F17-T15: the created id is marked as a fresh draft (freshDraftRegistry) —
 * an abandoned create is discarded by the detail card (Cancel / leave unsaved).
 *
 * Thin shell — the reply reading rule lives in `dashboardModel.readCreateReply`
 * (so it is unit-tested DOM-free), the request in `dashboardApi.useCreateManualProject`.
 */
import type { JSX } from 'react';
import { useToast } from '../ui/ToastContext.js';
import { useCreateManualProject } from './dashboardApi.js';
import { CREATE_ERROR_FALLBACK, readCreateReply } from './dashboardModel.js';
import { markFreshDraft } from '../project/freshDraftRegistry.js';

export interface NewProjectButtonProps {
  /** Called with the new project id on a successful create (opens its card). */
  readonly onCreated: (projectId: number) => void;
  /** Test-id (the e2e references `new-project-btn`). */
  readonly testId?: string;
}

export function NewProjectButton({
  onCreated,
  testId = 'new-project-btn',
}: NewProjectButtonProps): JSX.Element {
  const toast = useToast();
  const mutation = useCreateManualProject();
  const creating = mutation.isPending;

  function onClick(): void {
    if (creating) return;
    mutation.mutate(undefined, {
      onSuccess: (reply) => {
        const outcome = readCreateReply(reply);
        if (outcome.ok && outcome.projectId !== undefined) {
          toast.success('New project created');
          // E12-F17-T15 — the row exists already (legacy parity); mark it as a
          // fresh draft so Cancel / leaving the card without a Save discards it.
          markFreshDraft(outcome.projectId);
          onCreated(outcome.projectId);
        } else {
          toast.error(outcome.error ?? CREATE_ERROR_FALLBACK);
        }
      },
      onError: () => {
        toast.error(CREATE_ERROR_FALLBACK);
      },
    });
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={creating}
      className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-surface transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
    >
      {creating ? (
        <>
          <i
            className="fa-solid fa-spinner fa-spin"
            data-testid={`${testId}-spinner`}
            aria-hidden
          />
          Creating…
        </>
      ) : (
        <>
          <i className="fa-solid fa-plus" aria-hidden />
          New Project
        </>
      )}
    </button>
  );
}

export default NewProjectButton;
